import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Button, LoadingOverlay, TextInput } from '@mantine/core';
import { LOGIN_PATH } from '@/lib/navigation';
import api from '@/services/api-service';
import logoUrl from '/images/favicon.svg';

interface BusinessInfo {
    id: number;
    name: string;
    address: string | null;
}

export default function CreateAccountPage(): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState<'initial' | 'user-exists' | 'no-business-found' | 'business-found' | 'success'>('initial');
    const [business, setBusiness] = useState<BusinessInfo | null>(null);
    const [devMagicLink, setDevMagicLink] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleCheckEmail = async () => {
        if (!email) {
            setError('email-required');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const { data, error: apiError } = await api.onboarding.check(email);
            if (apiError) {
                throw new Error(typeof apiError === 'string' ? apiError : apiError.message || 'Check failed');
            }

            if (data.status === 'user-exists') {
                setStatus('user-exists');
            } else if (data.status === 'business-found') {
                setBusiness(data.business);
                setStatus('business-found');
            } else {
                setStatus('no-business-found');
            }
        } catch (err) {
            setError((err as Error).message || 'Failed to check email. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmOnboarding = async () => {
        if (!business) return;
        setIsLoading(true);
        setError('');
        try {
            const { data, error: apiError } = await api.onboarding.confirm(email, business.id);
            if (apiError) {
                throw new Error(typeof apiError === 'string' ? apiError : apiError.message || 'Signup failed');
            }

            setSuccessMessage('magic-link-sent-description');
            if (data.devMagicLink) {
                setDevMagicLink(data.devMagicLink);
            }
            setStatus('success');
        } catch (err) {
            setError((err as Error).message || 'Failed to create account. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setStatus('initial');
        setBusiness(null);
        setError('');
        setSuccessMessage('');
        setDevMagicLink('');
    };

    return (
        <div className="min-h-screen flex flex-col relative">
            <LoadingOverlay visible={isLoading} loaderProps={{ size: 'md', color: 'black' }} />

            {/* Logo in top left */}
            <div className="p-6 flex items-end gap-2">
                <img src={logoUrl} className="w-10" alt="Logo" />
                <div className="text-3xl font-semibold text-gray-900">
                    Turizi
                </div>
            </div>

            {/* Centered form */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-md px-8">
                    <div className="space-y-6">
                        {status === 'initial' && (
                            <>
                                <div>
                                    <h1 className="text-2xl font-normal text-gray-900">
                                        {t('create_account')}
                                    </h1>
                                    <p className="text-sm text-gray-600 mt-2">
                                        {t('create_account_description')}
                                    </p>
                                </div>

                                {error && (
                                    <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 border border-red-200">
                                        {t(error)}
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <TextInput
                                        label={t('email')}
                                        autoFocus
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleCheckEmail();
                                            }
                                        }}
                                        autoComplete="email"
                                        size="lg"
                                        placeholder="you@example.com"
                                    />
                                    <div className="pt-2">
                                        <Button
                                            fullWidth
                                            size="md"
                                            color="black"
                                            onClick={handleCheckEmail}
                                        >
                                            {t('continue')}
                                        </Button>
                                    </div>
                                    <div className="text-center pt-2">
                                        <Link
                                            to={LOGIN_PATH}
                                            className="text-sm font-semibold text-gray-500 hover:text-black transition-colors"
                                        >
                                            {t('back_to_login')}
                                        </Link>
                                    </div>
                                </div>
                            </>
                        )}

                        {status === 'user-exists' && (
                            <div className="space-y-4">
                                <h1 className="text-2xl font-normal text-gray-900">
                                    {t('user_exists_title')}
                                </h1>
                                <p className="text-sm text-gray-600">
                                    {t('user_exists_desc')}
                                </p>
                                <Button
                                    fullWidth
                                    size="md"
                                    color="black"
                                    onClick={() => navigate(LOGIN_PATH + `?email=${encodeURIComponent(email)}`)}
                                >
                                    {t('back_to_login')}
                                </Button>
                            </div>
                        )}

                        {status === 'no-business-found' && (
                            <div className="space-y-4">
                                <h1 className="text-2xl font-normal text-gray-900 text-red-600">
                                    {t('no_business_found_title')}
                                </h1>
                                <p className="text-sm text-gray-600">
                                    {t('no_business_found_desc')}
                                </p>
                                <div className="space-y-2 pt-2">
                                    <Button
                                        fullWidth
                                        size="md"
                                        variant="outline"
                                        color="black"
                                        onClick={resetForm}
                                    >
                                        {t('try_another_email')}
                                    </Button>
                                    <Button
                                        fullWidth
                                        size="md"
                                        color="black"
                                        onClick={() => navigate(LOGIN_PATH)}
                                    >
                                        {t('back_to_login')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {status === 'business-found' && business && (
                            <div className="space-y-6">
                                <div>
                                    <h1 className="text-2xl font-normal text-gray-900">
                                        {t('business_found_title')}
                                    </h1>
                                    <p className="text-sm text-gray-600 mt-2">
                                        {t('business_found_desc')}
                                    </p>
                                </div>

                                {error && (
                                    <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 border border-red-200">
                                        {t(error)}
                                    </div>
                                )}

                                <div className="p-5 rounded-lg border border-gray-200 bg-gray-50/50 space-y-2">
                                    <p className="font-semibold text-gray-900">{business.name}</p>
                                    {business.address && (
                                        <p className="text-sm text-gray-500">{business.address}</p>
                                    )}
                                </div>

                                <div className="text-center font-medium text-gray-800">
                                    {t('are_you_part_of_business')}
                                </div>

                                <div className="space-y-3 pt-2">
                                    <Button
                                        fullWidth
                                        size="md"
                                        color="black"
                                        onClick={handleConfirmOnboarding}
                                    >
                                        {t('yes_create_account')}
                                    </Button>
                                    <Button
                                        fullWidth
                                        size="md"
                                        variant="outline"
                                        color="black"
                                        onClick={resetForm}
                                    >
                                        {t('no_go_back')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {status === 'success' && (
                            <div className="space-y-6">
                                <div>
                                    <h1 className="text-2xl font-normal text-gray-900 text-emerald-600">
                                        {t('account_created_title')}
                                    </h1>
                                    <p className="text-sm text-gray-600 mt-2">
                                        {t(successMessage)}
                                    </p>
                                </div>

                                {devMagicLink && (
                                    <div className="p-4 text-sm text-amber-800 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
                                        <p className="font-semibold">Development Mode Helper:</p>
                                        <p className="text-xs break-all">Clicking below lets you bypass Resend email config in local development.</p>
                                        <Button 
                                            component="a" 
                                            href={devMagicLink} 
                                            size="xs" 
                                            color="amber"
                                            fullWidth
                                        >
                                            Click to Instantly Sign In
                                        </Button>
                                    </div>
                                )}

                                <Button
                                    fullWidth
                                    size="md"
                                    color="black"
                                    onClick={() => navigate(LOGIN_PATH)}
                                >
                                    {t('back_to_login')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
